import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type StudioTheme = "light" | "dark";
export type StudioDensity = "comfortable" | "compact";

interface AppearanceState {
  theme: StudioTheme;
  density: StudioDensity;
}

interface AppearanceValue extends AppearanceState {
  setTheme: (theme: StudioTheme) => void;
  setDensity: (density: StudioDensity) => void;
  toggleTheme: () => void;
  toggleDensity: () => void;
}

/** Non-protected UI preference key: cleared never, carries no session data. */
const APPEARANCE_STORAGE_KEY = "jacqos.web_studio.ui.appearance";

const AppearanceContext = createContext<AppearanceValue | null>(null);

function readStoredAppearance(): AppearanceState {
  const fallback: AppearanceState = { theme: "light", density: "comfortable" };
  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) {
      return fallback;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return fallback;
    }
    const record = parsed as Record<string, unknown>;
    return {
      theme: record.theme === "dark" ? "dark" : "light",
      density: record.density === "compact" ? "compact" : "comfortable",
    };
  } catch {
    return fallback;
  }
}

function applyAppearance(state: AppearanceState): void {
  const root = document.documentElement;
  if (state.theme === "dark") {
    root.setAttribute("data-theme", "dark");
  } else {
    root.removeAttribute("data-theme");
  }
  if (state.density === "compact") {
    root.setAttribute("data-density", "compact");
  } else {
    root.removeAttribute("data-density");
  }
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AppearanceState>(() => readStoredAppearance());

  useEffect(() => {
    applyAppearance(state);
    try {
      window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Preference persistence is best-effort; the session state still applies.
    }
  }, [state]);

  const setTheme = useCallback(
    (theme: StudioTheme) => setState((current) => ({ ...current, theme })),
    [],
  );
  const setDensity = useCallback(
    (density: StudioDensity) => setState((current) => ({ ...current, density })),
    [],
  );
  const toggleTheme = useCallback(
    () =>
      setState((current) => ({
        ...current,
        theme: current.theme === "dark" ? "light" : "dark",
      })),
    [],
  );
  const toggleDensity = useCallback(
    () =>
      setState((current) => ({
        ...current,
        density: current.density === "compact" ? "comfortable" : "compact",
      })),
    [],
  );

  const value = useMemo(
    () => ({ ...state, setTheme, setDensity, toggleTheme, toggleDensity }),
    [state, setTheme, setDensity, toggleTheme, toggleDensity],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceValue {
  const value = useContext(AppearanceContext);
  if (!value) {
    throw new Error("useAppearance requires ThemeProvider above the tree");
  }
  return value;
}
